import * as React from "react";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAccountingAuditLogs } from "@/hooks/useAccounting";
import { pickAssortedBadgeClass } from "@/lib/badge-tones";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ACTION_LABELS: Record<string, string> = {
  credit_limit_update: "Credit Limit",
  tier_update: "Tier Change",
  enterprise_status_update: "Enterprise Status",
  payment_terms_update: "Payment Terms",
  points_adjustment: "Points Adjustment",
};

function formatChanges(details: any): string {
  if (!details) return "—";
  const parts: string[] = [];

  if (details.changes) {
    for (const [key, val] of Object.entries(details.changes)) {
      const v = val as any;
      if (v && typeof v === "object" && ("old" in v || "new" in v || "from" in v || "to" in v)) {
        parts.push(`${key}: ${v.from ?? v.old ?? "—"} → ${v.to ?? v.new ?? "—"}`);
      }
    }
  }
  if (details.oldValue !== undefined || details.newValue !== undefined) {
    parts.push(`Changed: ${details.oldValue ?? "—"} → ${details.newValue ?? "—"}`);
  }
  if (details.reason) parts.push(`Reason: ${details.reason}`);
  if (details.amount !== undefined) parts.push(`Amount: ${details.amount}`);
  if (details.notes) parts.push(`Notes: ${details.notes}`);

  if (parts.length > 0) return parts.join(" · ");

  try {
    const safe = Object.entries(details)
      .filter(([key]) => !["performedBy", "timestamp", "email", "userId"].includes(key))
      .map(([key, value]) => `${key}: ${String(value)}`)
      .join(" · ");
    return safe || "—";
  } catch {
    return "—";
  }
}

export function AuditLogTab() {
  const [actionFilter, setActionFilter] = React.useState("all");
  const selectedAction = actionFilter === "all" ? undefined : actionFilter;
  const logs = useAccountingAuditLogs(selectedAction);

  const entries = React.useMemo(() => {
    if (!logs) return [];
    return logs.logs ?? logs;
  }, [logs]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Audit Log</CardTitle>
              <CardDescription>
                All accounting changes for compliance
              </CardDescription>
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="credit_limit_update">Credit Limit</SelectItem>
                <SelectItem value="tier_update">Tier Changes</SelectItem>
                <SelectItem value="enterprise_status_update">
                  Enterprise Status
                </SelectItem>
                <SelectItem value="payment_terms_update">Payment Terms</SelectItem>
                <SelectItem value="points_adjustment">
                  Points Adjustments
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {logs === undefined ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading audit logs...</div>
          ) : entries.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No audit log entries found
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-muted-foreground hover:bg-transparent">
                  <TableHead className="font-medium">Time</TableHead>
                  <TableHead className="font-medium">Action</TableHead>
                  <TableHead className="font-medium">User</TableHead>
                  <TableHead className="font-medium">Changes</TableHead>
                  <TableHead className="font-medium">Admin</TableHead>
                  <TableHead className="font-medium">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry: any, idx: number) => (
                  <TableRow key={entry._id ?? idx}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {entry.timestampMs
                        ? formatDate(entry.timestampMs)
                        : entry._creationTime
                          ? formatDate(entry._creationTime)
                          : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          pickAssortedBadgeClass(entry.action),
                          "text-xs",
                        )}
                      >
                        {ACTION_LABELS[entry.action] || entry.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {entry.user
                        ? entry.user.company ||
                          entry.user.name ||
                          entry.user.email ||
                          "—"
                        : "—"}
                    </TableCell>
                    <TableCell className="max-w-[420px] text-xs text-muted-foreground">
                      <span className="line-clamp-2">
                        {formatChanges(entry.details)}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {entry.details?.performedBy ||
                        entry.performedBy ||
                        "System"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          entry.success === false
                            ? "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200"
                            : "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200",
                          "text-xs",
                        )}
                      >
                        {entry.success === false ? "Failed" : "Success"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
